import { createFileRoute } from "@tanstack/react-router";
import { ArticlePage } from "@/components/ArticlePage";
import { articleSchemas } from "@/lib/seo";

const SITE = "https://shopify-freelancer.net";
const PATH = "/ratgeber/shopify-mobile-optimierung";
const TITLE = "Shopify Mobile Optimierung: mehr Umsatz am Handy";
const DESCRIPTION =
  "Mobile bringt den meisten Traffic und die schlechteste Conversion-Rate. Die konkreten Hebel für Navigation, Produktseite, Warenkorb und Checkout am Smartphone.";

const faqs = [
  {
    q: "Warum konvertiert Mobile schlechter als Desktop?",
    a: "Nicht wegen der Bildschirmgröße, sondern wegen Kontext und Aufwand: unterwegs, abgelenkt, mit Daumenbedienung und Tippaufwand. Jede Eingabe, jeder unnötige Klick kostet mobil ein Vielfaches.",
  },
  {
    q: "Reicht ein responsives Theme?",
    a: "Nein. Responsiv heißt nur, dass alles passt, nicht, dass es sich gut bedienen lässt. Reihenfolge der Inhalte, Größe der Touch-Ziele und Zahl der Formularfelder entscheiden über den Umsatz.",
  },
  {
    q: "Was bringt am schnellsten etwas?",
    a: "Express-Zahlarten sichtbar platzieren, den Kaufen-Button mobil dauerhaft erreichbar machen und Versandkosten früh nennen. Diese drei Eingriffe brauchen wenig Aufwand und wirken auf jeder Produktseite.",
  },
  {
    q: "Braucht mein Shop eine App?",
    a: "Fast nie. Eine gut gebaute mobile Website erreicht mehr Kunden zu einem Bruchteil der Kosten. Eine App lohnt sich erst bei hoher Wiederkaufrate und einer bestehenden Stammkundschaft.",
  },
];

export const Route = createFileRoute("/ratgeber/shopify-mobile-optimierung")({
  component: Page,
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: `${SITE}${PATH}` },
      { property: "og:type", content: "article" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: `${SITE}${PATH}` }],
    scripts: articleSchemas({ title: TITLE, description: DESCRIPTION, path: PATH, faqs }),
  }),
});

function Page() {
  return (
    <ArticlePage
      kicker="Ratgeber · Mobile"
      title="Shopify Mobile Optimierung: der Daumen entscheidet"
      intro="Zwischen 70 und 80 Prozent der Besucher kommen mobil, konvertieren aber deutlich schlechter als am Desktop. Genau in dieser Lücke liegt bei den meisten Shops der größte unbearbeitete Umsatz."
      source="ratgeber-mobile"
      sections={[
        {
          h: "Die Lücke sichtbar machen",
          p: [
            "Vergleiche zuerst die Kaufabschlussrate nach Gerät. Liegt Mobile bei weniger als der Hälfte des Desktop-Werts, ist das kein Naturgesetz, sondern ein Befund.",
            "Rechne die Lücke in Geld um: Mobile-Sessions × Differenz der Conversion-Rate × Warenkorb. Diese Zahl entscheidet, wie viel Aufwand gerechtfertigt ist.",
          ],
        },
        {
          h: "Oberhalb der Falz gehört das Nötigste",
          p: [
            "Auf dem Handy sieht der Kunde zuerst einen Ausschnitt von etwa der Größe einer Postkarte. Was dort steht, entscheidet über alles Weitere.",
          ],
          list: [
            "Produktname, Preis und Verfügbarkeit ohne Scrollen",
            "Ein aussagekräftiges Bild statt eines Karussells mit acht Varianten",
            "Kaufen-Button in Daumenreichweite, dauerhaft erreichbar",
            "Lieferzeit und Versandkosten als Textzeile, nicht in einem Aufklapper",
            "Bewertungen als kompakte Zeile, nicht als Block",
          ],
        },
        {
          h: "Jede Eingabe ist eine Hürde",
          p: [
            "Adressfelder, Gutscheincodes, Pflicht-Accounts: Alles, was Tippen erfordert, kostet mobil überproportional. Autofill, passende Tastaturtypen und Adress-Vervollständigung sind kein Komfort, sondern Umsatz.",
            "Express-Zahlarten gehören über das Formular, nicht darunter. Wer mit hinterlegten Daten in drei Sekunden kaufen kann, tippt keine Adresse mehr ein.",
          ],
        },
        {
          h: "Navigation für Suchende, nicht für Stöbernde",
          p: [
            "Mobile Besucher suchen häufiger gezielt. Eine sichtbare Suche mit Vorschlägen und Bildern schlägt jedes tief verschachtelte Menü.",
            "Filter in Kollektionen gehören als feste Leiste an den oberen Rand, mit Anzahl der Treffer, damit klar ist, ob sich das Filtern lohnt.",
          ],
        },
        {
          h: "Mobil testen heißt mobil messen",
          p: [
            "Werte jeden Test getrennt nach Gerät aus. Eine Änderung, die am Desktop drei Prozent bringt und mobil fünf Prozent kostet, ist im Gesamtergebnis unsichtbar und trotzdem ein Verlustgeschäft.",
          ],
        },
      ]}
      faqs={faqs}
      related={[
        { to: "/blog/shopify-checkout-optimieren", label: "Checkout optimieren" },
        { to: "/ratgeber/shopify-pagespeed-optimieren", label: "Pagespeed verbessern" },
        { to: "/roi-rechner", label: "Potenzial berechnen" },
      ]}
    />
  );
}
